import Checkbox from '@/components/Checkbox/Checkbox';
import { toast } from '@/components/toast';
import { TermsUrl } from '@/environment';
import styled from '@emotion/styled';
import { TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useCreateAvatarStore } from '../../common/context';
import { CreateAvatarStore } from '../../CreatePage/CreateAvatarStore';
import { validateInputLanguage } from './validate-username';

const ConsentStepContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 32px 0 40px;
    width: 560px;
    margin: 0 auto;
`;
const ProvideConsentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    position: relative;
    padding-bottom: 154px;
`;

const Title = styled.h1`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.56px;
`;

const Description = styled.span`
    color: ${({ theme }) => theme.colors.secondary[700]};
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0.15px;
`;

const Subtitle = styled.span`
    color: ${({ theme }) => theme.colors.black[90]};
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.28px;
    margin-block-end: 8px;
`;

const Subcontainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 540px;
    margin-block-start: 52px;
`;

const TermsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-block-start: 26px;
    align-items: flex-start;
`;

const TermsText = styled.span`
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
`;
const LinkText = styled.label`
    color: #497cff;
    cursor: pointer;
`;

const StyledTextField = styled(TextField)`
    height: 34px;
    background-color: white;

    .MuiOutlinedInput-root fieldset.MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.black[300]} !important;
        border-width: 1px;
        border-radius: 8px;
    }

    .MuiOutlinedInput-root.Mui-focused fieldset.MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.secondary.main} !important;
    }

    .MuiInputBase-root {
        height: 34px;
    }
`;
const USER_NAME_PLACEHOLDER = '[user name]';

export const MinimalConsent = observer(() => {
    const createAvatarStore = useCreateAvatarStore() as CreateAvatarStore;
    const [errorToastId, setErrorToastId] = useState<string | number | null>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isValid = validateInputLanguage(e.target.value);
        if (!isValid) {
            const id = toast.error({
                message: 'Please use only English characters',
                prevId: errorToastId,
            });
            setErrorToastId(id);
            return;
        }
        createAvatarStore.setUsername(e.target.value);
    };

    return (
        <ConsentStepContainer>
            <ProvideConsentContainer>
                <Title>Give consent</Title>
                <Description>
                    To prevent technology misuse, please confirm the person in the video is you by ticking the consent checkbox below.
                </Description>

                <Subcontainer>
                    <Subtitle>Name your Avatar</Subtitle>
                    <StyledTextField
                        data-testid="create-avatar-name-input"
                        type="text"
                        size="small"
                        value={createAvatarStore.username}
                        onChange={handleInputChange}
                        placeholder={USER_NAME_PLACEHOLDER}
                        fullWidth
                    />

                    <TermsContainer>
                        <Checkbox
                            testId="create-avatar-consent-checkbox"
                            checked={createAvatarStore.isConsentAgreed}
                            onClick={() => createAvatarStore.setIsConsentAgreed(!createAvatarStore.isConsentAgreed)}
                        />
                        <TermsText>
                            By checking this box I confirm that I have all the necessary rights or consents to use this footage and voice
                            recording for creating an Avatar from my 'User Submission' all in accordance with the{' '}
                            <LinkText onClick={() => window.open(TermsUrl, '_blank')}>Terms of Use.</LinkText>
                        </TermsText>
                    </TermsContainer>
                </Subcontainer>
            </ProvideConsentContainer>
        </ConsentStepContainer>
    );
});
